import React, { useEffect, useState } from "react";

import Input from "../app/Input";
import Checkbox from "../common/Checkbox";
import Button from "../app/Button";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as actions from "src/redux/slices/onboardingSlice";
import { Link } from "react-router-dom";
import { APP_LINKS } from "src/constants/common";
import analytics from "src/helpers/analytics";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";
import environment from "src/constants/environment";

interface MobileNumberProps {
  title: string;
  description: string;
  mobileNumber: string;
  onSuccess: (mobileNumber: string) => void;
  support_program_key?: string;
}

const MobileNumber: React.FC<MobileNumberProps> = ({
  title,
  description,
  mobileNumber,
  onSuccess,
  support_program_key,
}) => {
  const [phoneNumber, setPhoneNumber] = useState(mobileNumber || "");
  const [isChecked, setIsChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useAppDispatch();
  const { sendOtp } = useAppSelector((state) => state.onboarding);

  const isProd = environment.ENV === "prod";

  const isValidPhoneNumber = (num: string) => /^[6-9]\d{9}$/.test(num); // simple Indian mobile check

  const handleSendOtp = async () => {
    if (isProd && !isValidPhoneNumber(phoneNumber)) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }

    setError(null);

    // Event: SEND_OTP_CLICKED
    analytics.trackEvent(EVENT_NAMES.SEND_OTP_CLICKED, {
      mobile_number: phoneNumber,
      screen_name: SCREEN_NAMES.MOBILE_NUMBER_CAPTURE_SCREEN,
      support_program_key: support_program_key,
    });

    // Set User Attributes
    analytics.setUserAttributes({
      contact_no: phoneNumber,
    });

    const payload = {
      payload: {
        contactNo: phoneNumber,
        otpType: "LOGIN",
      },
      headers: {
        userFrom: "PSP",
        source: "GoodFlipCare",
      },
    };

    new Promise((resolve, reject) => {
      dispatch(actions.sendOtpRequest({ payload, resolve, reject }));
    });
  };

  useEffect(() => {
    analytics.pageView(SCREEN_NAMES.MOBILE_NUMBER_CAPTURE_SCREEN);
  }, []);

  useEffect(() => {
    if (sendOtp?.data?.code === 1) {
      analytics.trackEvent(EVENT_NAMES.SEND_OTP_SUCCESS, {
        mobile_number: phoneNumber,
        screen_name: SCREEN_NAMES.MOBILE_NUMBER_CAPTURE_SCREEN,
        support_program_key: support_program_key,
      });
      resetSendOtp();
      onSuccess(phoneNumber);
    } else if (sendOtp?.data?.code === 0) {
      console.error("Error sending OTP:", sendOtp?.data?.message);
      analytics.trackEvent(EVENT_NAMES.SEND_OTP_ERROR, {
        mobile_number: phoneNumber,
        screen_name: SCREEN_NAMES.MOBILE_NUMBER_CAPTURE_SCREEN,
        support_program_key: support_program_key,
      });
      setError(sendOtp?.data?.message || "Something went wrong!");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendOtp]);

  const resetSendOtp = () => {
    new Promise((resolve, reject) => {
      dispatch(actions.resetSendOtp({ resolve, reject }));
    });
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="p-4 flex-1 min-h-0 overflow-y-auto">
        <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-2">
          {title}
        </h1>
        <p className="text-[var(--text-secondary)]">{description}</p>

        <Input
          name="mobile_number"
          type="text"
          label="Phone Number"
          placeholder="Enter your phone number"
          fullWidth
          maxLength={10}
          startAdornment={<p className="text-gray-500">+91</p>}
          autoFocus
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          error={!!error}
          helperText={error || undefined}
          className="mt-4"
        />

        <Checkbox
          label={
            <p>
              I agree to the{" "}
              <Link
                to={APP_LINKS?.TERMS_AND_CONDITIONS}
                target="_blank"
                className="text-[var(--app-primary)] underline"
              >
                Terms & Conditions
              </Link>{" "}
              and{" "}
              <Link
                to={APP_LINKS?.PRIVACY_POLICY}
                target="_blank"
                className="text-[var(--app-primary)] underline"
              >
                Privacy Policy
              </Link>
            </p>
          }
          checked={isChecked}
          onChange={() => {
            setIsChecked(!isChecked);
            analytics.trackEvent(
              EVENT_NAMES.AGREE_TO_TERMS_AND_CONDITIONS_CLICKED,
              {
                is_agreed: !isChecked,
                screen_name: SCREEN_NAMES.MOBILE_NUMBER_CAPTURE_SCREEN,
                support_program_key,
              },
            );
          }}
          className="mt-4"
        />
      </div>

      <div className="bg-white border-t border-gray-200 p-4 mt-auto">
        <Button
          fullWidth
          onClick={handleSendOtp}
          disabled={
            (isProd
              ? !isValidPhoneNumber(phoneNumber)
              : phoneNumber?.length !== 10) ||
            sendOtp?.isLoading ||
            !isChecked
          }
          loading={sendOtp?.isLoading}
        >
          Send OTP
        </Button>
      </div>
    </div>
  );
};

export default MobileNumber;
